import { Component, OnInit } from '@angular/core';
import { Store } from '@ngxs/store';
import { Observable, delay, filter, firstValueFrom, map, skip, takeUntil } from 'rxjs';
import { concat } from 'lodash';

import { BrandState } from '@hiptraveler/data-access/brand';
import { ExperienceFinderStateService } from '@hiptraveler/features/experience-finder';

// Legacy functions
import { Experience } from '@hiptraveler/data-access/api';
import { ListItem } from '../../../modules/shell--search-page-root/filters/list-items';
import { getFiltersStateService } from '../search-filter-ref';
import { isWidget } from '@hiptraveler/common';

@Component({
  selector: 'travel-style',
  template: `
    <filters></filters>
    <item-selection *ngIf="itemSelectionVisibility$ | async"></item-selection>
    <action-button
      [marginY]="-36"
      (click)="stateService.dialogView$$.next('plan-trip')">
      {{ 'exp-finder.button-text-next' | translate }}
    </action-button>
  `,
  host: { class: 'travel-style-host selection-view-host' }
})
export class TravelStyleComponent implements OnInit {

  constructor(
    private store: Store,
    public stateService: ExperienceFinderStateService
  ) { }

  get itemSelectionVisibility$(): Observable<boolean> {
    return this.store.select(BrandState.experiences).pipe(
      map(e => !!e?.length)
    )
  }

  ngOnInit(): void {

    // Legacy functions
    this.legacyFunctions();
  }

  // Legacy functions
  private async legacyFunctions(): Promise<void> {

    if (isWidget()) return;

    await firstValueFrom(this.stateService.overlayState$.pipe(filter(Boolean)));

    // --- ON INIT: updates expfinder from the url query params

    await firstValueFrom(this.stateService.overlayState$.pipe(filter(Boolean)));
    
    const { activities: searchAct, experiences, travelStyle } = Object.fromEntries(
      new URLSearchParams(window.location.search)
    );
    this.stateService.selectedFilters$$.next(concat(
      searchAct?.split(','), experiences?.split(','), travelStyle?.split(','),
    ));

    const filtersState = getFiltersStateService();

    this.store.select(BrandState.clientProfile).pipe(
      delay(100),
      filter(e => !!e?.travelStyle?.filter(Boolean)?.length),
      takeUntil( this.stateService.overlayState$.pipe(map(e => !e), skip(1)) )
    ).subscribe(clientProfile => {
      const travelStyle = clientProfile!.travelStyle;
      this.stateService.selectedFilters$$.next(travelStyle || []);
    });

    // --- TRAVEL STYLES: expfinder updates the search filters

    const brandExperiences = this.store.selectSnapshot(BrandState.experiences) || [];
    const activities = brandExperiences.map((experience: Partial<Experience>) => ({
      type: experience?.category === 'activity' ? 'activities' 
        : experience?.category === 'experience' ? 'exp' : 'travelStyle',
      name: experience?.name || '',
      value: experience?.code || ''
    })) as ListItem[];
    
    this.stateService.selectedFilters$$.asObservable().pipe(
      takeUntil( this.stateService.overlayState$.pipe(map(e => !e), skip(1)) )
    ).subscribe((filters) => {
    
      const listItems = filters.map(filter => activities.find(e => e.value === filter));

      const selectedItems = listItems.filter(e => e?.type === 'activities') as ListItem[];
      const selectedTravelStyles = listItems.filter(e => e?.type !== 'activities') as ListItem[];

      const _selectedItems = filtersState.selectedItems.filter(e => e.type === 'activities');
      const _selectedTravelStyles = filtersState.selectedTravelStyles;

      if (_selectedItems.length !== selectedItems.length) {
        filtersState.selectedItems$$.next(selectedItems);
      }
      
      if (_selectedTravelStyles.length !== selectedTravelStyles.length) {
        filtersState.selectedTravelStyles$$.next(selectedTravelStyles);
      }
    });
    
    // --- TRAVEL STYLES: search filters updates the expfinder

    filtersState.selectedTravelStyleKeys$.pipe(
      takeUntil( this.stateService.overlayState$.pipe(map(e => !e), skip(1)) )
    ).subscribe((res) => {
      const selectedFilters = res.concat(filtersState.selectedItems.map(e => e.value));
      this.stateService.selectedFilters$$.next(selectedFilters)
    });

    filtersState.selectedItemKeys$.pipe(
      takeUntil( this.stateService.overlayState$.pipe(map(e => !e), skip(1)) )
    ).subscribe((res) => {
      const selectedFilters = res.concat(filtersState.selectedTravelStyles.map(e => e.value))
      this.stateService.selectedFilters$$.next(selectedFilters)
    });
  }


}
